import {
	SendOutlined,
	PaperClipOutlined,
	FilePdfOutlined,
	FileWordOutlined,
	FileExcelOutlined,
	FilePptOutlined,
	FileOutlined,
	DownloadOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import {
	Avatar,
	Button,
	Col,
	Form,
	Image,
	Input,
	message,
	Modal,
	Row,
	Tooltip,
} from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { BaseAnimationComponent, TapAnimationComponent } from "components";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSocketListens } from "hooks/useSocket";

const { TextArea } = Input;

const getFileMetadata = (url) => {
	try {
		const parsed = new URL(url, window.location.origin);
		const originalName = parsed.searchParams.get("originalName") || "添付ファイル";
		const fileSize = parsed.searchParams.get("fileSize") || "";
		return { originalName, fileSize };
	} catch (e) {
		return { originalName: "添付ファイル", fileSize: "" };
	}
};

const getMediaUrl = (contents) => {
	if (!contents) return "";
	let pathAndQuery = contents;
	if (contents.startsWith("http://") || contents.startsWith("https://")) {
		try {
			const parsedUrl = new URL(contents);
			pathAndQuery = parsedUrl.pathname + parsedUrl.search;
		} catch (e) {
			return contents;
		}
	}
	const baseUrl = API.SITE_URL.endsWith("/") ? API.SITE_URL.slice(0, -1) : API.SITE_URL;
	const cleanPath = pathAndQuery.startsWith("/") ? pathAndQuery : `/${pathAndQuery}`;
	return `${baseUrl}${cleanPath}`;
};

const truncateFilename = (name) => {
	if (!name) return "";
	if (name.length <= 18) return name;
	const ext = name.split('.').pop();
	const base = name.substring(0, 10);
	return `${base}....${ext}`;
};

const renderDocumentCard = (url, type) => {
	const { originalName, fileSize } = getFileMetadata(url);
	const fullUrl = getMediaUrl(url);
	return (
		<div
			className="p-3 cursor-pointer"
			style={{
				backgroundColor: "#f0f2f5",
				color: "#595959",
				maxWidth: "240px",
				display: "inline-block",
				textAlign: "left",
				borderRadius: "8px",
				boxShadow: "none"
			}}
			onClick={() => window.open(fullUrl, "_blank")}
		>
			<div className="flex items-center mb-1 font-medium" style={{ fontSize: "13px" }}>
				<DownloadOutlined style={{ marginRight: "6px", fontSize: "14px", color: "#595959" }} />
				<span className="truncate" title={originalName} style={{ maxWidth: "180px", color: "#595959" }}>
					{originalName}
				</span>
			</div>
			<div style={{ fontSize: "11px", color: "#8c8c8c", paddingLeft: "20px" }}>
				サイズ: {fileSize || "不明"}
			</div>
		</div>
	);
};

const ChatModal = (props) => {
	const { publicSettings, currentMember, isChatModalVisible, hideChatModal } =
		props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const currentMemberRef = useRef();
	const messageContainerRef = useRef(null);
	const [chatForm] = Form.useForm();

	const [chats, setChats] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const fileInputRef = useRef(null);
	const textValue = Form.useWatch("contents", chatForm);

	const scrollToMessageContainer = () => {
		if (messageContainerRef.current) {
			setTimeout(() => {
				messageContainerRef.current.scrollIntoView({ behavior: "smooth" });
			}, 100);
		}
	};

	const { refetch: refetchChats } = useQuery(
		[API.QUERY_KEY_ADMIN_CHATS, currentMember],
		() => API.ADMIN_GET_CHATS(currentMember),
		{
			enabled: !!currentMember && isChatModalVisible,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setChats(response?.data || []);
					scrollToMessageContainer();
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const sendChatMutation = useMutation(API.ADMIN_SEND_CHAT, {
		onSuccess: () => {
			message.success(COMMONS.SUCCESS_MESSAGE_SENT_MSG);
			queryClient.invalidateQueries({ queryKey: [API.QUERY_KEY_ADMIN_CHATS] });
			chatForm.resetFields();
			setSelectedFile(null);
			if (fileInputRef.current) fileInputRef.current.value = "";
		},
		onError: (error) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	useEffect(() => {
		currentMemberRef.current = currentMember;
	}, [currentMember]);

	useSocketListens([
		[
			COMMONS.SOCKET_EVENTS.CHAT_CREATED,
			async ({ memberId }) => {
				if (currentMemberRef.current?.memberId === memberId) {
					await refetchChats();
				}
			},
		],
	]);

	const handleChat = (data) => {
		if (selectedFile) {
			const formData = new FormData();
			formData.append("file", selectedFile);
			formData.append("memberId", currentMember?.memberId);
			sendChatMutation.mutate(formData);
		} else {
			const paramData = {
				contents: data?.contents,
				memberId: currentMember?.memberId,
			};

			sendChatMutation.mutate(paramData);
		}
	};

	return (
		<>
			<Modal
				title={`${
					currentMember?.lastName && currentMember?.firstName
						? `${currentMember?.lastName || "ー"} ${
								currentMember?.firstName || "ー"
							}`
						: currentMember?.displayName || "ー"
				}様`}
				open={isChatModalVisible}
				onCancel={hideChatModal}
				footer={null}
				centered
				destroyOnClose
			>
				<BaseAnimationComponent>
					<motion.div
						className="pb-4 overflow-auto"
						style={{
							minHeight: "60vh",
							maxHeight: "60vh",
						}}
					>
						{chats.map((chat) => (
							<Row key={chat?.chatId}>
								<Col span={22} offset={chat?.source === "user" ? 0 : 2}>
									<div className="m-1">
										{chat?.source === "user" ? (
											<>
												<div className="flex flex-row items-start justify-start">
													<div className="mr-1">
														<Avatar
															size={40}
															src={
																currentMember?.picUrl ? (
																	<Image
																		preview={false}
																		width={40}
																		height={40}
																		src={`${currentMember.picUrl}/small`}
																		fallback="/no-image.png"
																	/>
																) : (
																	<Image
																		src="/no-image.png"
																		width={40}
																		height={40}
																		preview={false}
																	/>
																)
															}
														/>
													</div>
													{chat?.contentType === COMMONS.MESSAGE_TYPE_TEXT ? (
														<div
															className="text-white rounded-lg p-2 cursor-pointer mr-1"
															style={{
																backgroundColor: COMMONS.CUSTOM_GREEN,
															}}
														>
															<span className="whitespace-pre-wrap">
																{chat?.contents || ""}
															</span>
														</div>
													) : chat?.contentType === "image" ? (
														<div className="mr-1">
															<Image
																src={getMediaUrl(chat?.contents)}
																width={200}
																className="rounded-lg"
															/>
														</div>
													) : chat?.contentType === "audio" ? (
														<div className="mr-1">
															<audio
																controls
																src={getMediaUrl(chat?.contents)}
																style={{ maxWidth: "240px" }}
															/>
														</div>
													) : chat?.contentType === "video" ? (
														<div className="mr-1">
															<video
																controls
																src={getMediaUrl(chat?.contents)}
																style={{ maxWidth: "240px", maxHeight: "180px" }}
																className="rounded-lg"
															/>
														</div>
													) : ["pdf", "word", "excel", "powerpoint"].includes(chat?.contentType) ? (
														<div className="mr-1">
															{renderDocumentCard(chat?.contents, chat?.contentType)}
														</div>
													) : (
														""
													)}
												</div>
												<div className="flex flex-row items-start justify-start">
													<span
														className="whitespace-pre-wrap"
														style={{
															fontSize: "10px",
															color: COMMONS.GRAY_COLOR,
															marginLeft: "45px",
														}}
													>
														{chat?.createdAt
															? moment(chat.createdAt).format(
																	"YYYY年M月D日 HH:mm",
																)
															: ""}
													</span>
												</div>
											</>
										) : (
											<>
												<div className="flex flex-row items-start justify-end">
													{chat?.contentType === COMMONS.MESSAGE_TYPE_TEXT ? (
														<div
															className="text-white rounded-lg p-2 cursor-pointer"
															style={{
																backgroundColor: publicSettings?.PRIMARY_COLOR,
															}}
														>
															<span className="whitespace-pre-wrap">
																{chat?.contents || ""}
															</span>
														</div>
													) : chat?.contentType === "image" ? (
														<div>
															<Image
																src={getMediaUrl(chat?.contents)}
																width={200}
																className="rounded-lg"
															/>
														</div>
													) : ["pdf", "word", "excel", "powerpoint"].includes(chat?.contentType) ? (
														<div>
															{renderDocumentCard(chat?.contents, chat?.contentType)}
														</div>
													) : (
														""
													)}
												</div>
												<div className="flex flex-row items-start justify-end">
													<span
														className="whitespace-pre-wrap"
														style={{
															fontSize: "10px",
															color: COMMONS.GRAY_COLOR,
														}}
													>
														{chat?.createdAt
															? moment(chat.createdAt).format(
																	"YYYY年M月D日 HH:mm",
																)
															: ""}
													</span>
												</div>
											</>
										)}
									</div>
								</Col>
							</Row>
						))}
						<div ref={messageContainerRef}></div>
					</motion.div>
					<div
						className="pt-2 border-t border-solid"
						style={{
							borderColor: "#d9d9d9",
						}}
					>
						<Row>
							<Col span={24}>
								<Form
									form={chatForm}
									onFinish={handleChat}
									size="large"
									colon={false}
									requiredMark={false}
									initialValues={{
										contents: undefined,
									}}
								>
									<Row align="middle" justify="space-between" className="mb-2" style={{ paddingBottom: "8px" }}>
										<Col>
											<input
												type="file"
												ref={fileInputRef}
												style={{ display: "none" }}
												accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
												onChange={(e) => {
													if (e.target.files && e.target.files.length > 0) {
														const file = e.target.files[0];
														if (file.size > 10 * 1024 * 1024) {
															message.error("ファイルサイズは10MB以下にしてください。");
															e.target.value = "";
															return;
														}
														setSelectedFile(file);
													}
												}}
											/>
											{selectedFile ? (
												<div
													style={{
														backgroundColor: "#999999",
														color: "#ffffff",
														padding: "4px 8px",
														borderRadius: "4px",
														fontSize: "12px",
														fontWeight: "bold",
														display: "flex",
														alignItems: "center",
														gap: "6px",
														cursor: "default"
													}}
												>
													<PaperClipOutlined style={{ color: "#ffffff", fontSize: "12px" }} />
													<span>{truncateFilename(selectedFile.name)}</span>
													<CloseOutlined
														style={{ color: "#ffffff", cursor: "pointer", fontSize: "12px", marginLeft: "4px" }}
														onClick={() => {
															setSelectedFile(null);
															if (fileInputRef.current) fileInputRef.current.value = "";
														}}
													/>
												</div>
											) : (
												<Button
													size="middle"
													icon={<PaperClipOutlined style={{ color: "#ffffff", fontSize: "12px" }} />}
													onClick={() => fileInputRef.current?.click()}
													disabled={!!(textValue && textValue.trim() !== "")}
													style={{
														backgroundColor: (textValue && textValue.trim() !== "") ? "#d9d9d9" : "#999999",
														borderColor: (textValue && textValue.trim() !== "") ? "#d9d9d9" : "#999999",
														borderRadius: "4px",
														color: "#ffffff",
														fontWeight: "bold",
														height: "32px",
														display: "flex",
														alignItems: "center",
														gap: "6px"
													}}
												>
													<span style={{ fontSize: "12px" }}>ファイルを選択</span>
												</Button>
											)}
										</Col>
										<Col>
											<Tooltip title="送信" placement="top">
												<TapAnimationComponent>
													<Button
														type="primary"
														style={{
															backgroundColor: "#999999",
															borderColor: "#999999",
															borderRadius: "4px",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															width: "36px",
															height: "32px",
															padding: 0
														}}
														icon={
															<SendOutlined
																style={{ color: "#ffffff", fontSize: "16px" }}
															/>
														}
														htmlType="submit"
														loading={sendChatMutation.isLoading}
													/>
												</TapAnimationComponent>
											</Tooltip>
										</Col>
									</Row>

									<Row>
										<Col span={24}>
											<Form.Item
												className="block"
												name="contents"
												style={{ marginBottom: 0 }}
												rules={[
													{
														required: !selectedFile,
														whitespace: true,
														message: "メッセージを入力してください",
													},
												]}
											>
												<TextArea
													allowClear
													bordered
													showCount
													autoSize={{ minRows: 4, maxRows: 8 }}
													maxLength={5000}
													placeholder={selectedFile ? "ファイル送信時はテキストを入力できません" : "メッセージを入力してください"}
													autoFocus={true}
													disabled={!!selectedFile}
													style={selectedFile ? { backgroundColor: "#f5f5f5" } : {}}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Form>
							</Col>
						</Row>
					</div>
				</BaseAnimationComponent>
			</Modal>
		</>
	);
};

export default ChatModal;
